import { useState } from "react";
import { AlertOctagon, X, Loader2 } from "lucide-react";

/**
 * Small dialog for capturing the free-text reason when marking a
 * patient or caregiver as fraudulent. The reason is required (min
 * 5 chars, enforced client-side + server-side).
 */
export default function FraudModal({ target, targetType, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const displayName =
    `${target.first_name || ""} ${target.last_name || ""}`.trim() ||
    target.email ||
    "this record";

  const canSubmit = reason.trim().length >= 5 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="fraud-modal"
    >
      <div className="admin-modal-panel bg-white rounded-2xl w-full max-w-lg">
        <div className="p-5 border-b border-[var(--sage)]/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <AlertOctagon size={20} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-['Outfit'] font-semibold text-[var(--charcoal)]">
                Mark as fraudulent
              </h2>
              <p className="text-xs text-[var(--olive)] mt-0.5">
                {targetType === "patient" ? "Patient" : "Caregiver"}:{" "}
                <span className="font-medium">{displayName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-[var(--sage)]/10 rounded-lg"
            aria-label="Close"
            data-testid="fraud-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-900 leading-relaxed">
            The row will be highlighted in the admin list and will be
            <strong> excluded from every CSV export </strong>
            (patients, caregivers, study interests). Any other record
            with the same device/IP fingerprint will be auto-tagged as
            &ldquo;same source&rdquo; for you to review.
          </div>

          <label className="block">
            <span className="text-sm font-medium text-[var(--charcoal)] mb-2 block">
              Reason (required, min. 5 characters)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g. Follow-up call confirmed condition not present. Applicant refused verification when asked."
              className="w-full rounded-lg border border-[var(--light-border)] p-3 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
              data-testid="fraud-modal-reason"
              autoFocus
            />
            <span className="text-xs text-[var(--olive)] mt-1 block">
              {reason.length}/1000 characters
            </span>
          </label>
        </div>

        <div className="p-5 border-t border-[var(--sage)]/10 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="btn-secondary px-4 py-2 text-sm"
            disabled={busy}
            data-testid="fraud-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="fraud-modal-confirm"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Mark as fraudulent
          </button>
        </div>
      </div>
    </div>
  );
}
